// Company Brain demo — scenario scripts (pure data, no logic).
// Всяка Conversation е чат с 1–2 реплики на потребителя + сценариен отговор.
// seeded → показва се като готова история в списъка; без seeded → чип в „Нов чат“ и се играе на живо.
// Вижте README.md в тази директория за подробности.

export type Confidence = 'висока' | 'средна' | 'ниска';

export interface Citation {
  label: string; // "Правило #4471"
  source: string; // "Мариела · 12.02.2026"
  detail?: string; // "прилагано 8×, потвърдено 8×, отменяно 0×"
}

export interface UserStep {
  kind: 'user';
  content: string;
  time?: string; // "09:14" — само за seeded разговори
  durationMs?: number; // пауза преди появата (по подразбиране: 200мс първа, 1400мс следващи)
}

export interface ToolCallStep {
  kind: 'tool';
  name: string; // "get_classification_rules"
  args: Record<string, string>;
  summary: string; // едноредов човешки резултат
  rows?: Record<string, string>[]; // опционална мини-таблица с резултати
  durationMs?: number; // по подразбиране: случайно 800–1800
}

export interface ThinkingStep {
  kind: 'thinking';
  label?: string; // "Преглеждам конвенциите на клиента…"
  durationMs?: number; // по подразбиране ~700
}

export interface Contradiction {
  severity: 'hard' | 'soft'; // 🔴 блокира / 🟡 предупреждава
  text: string;
  status?: 'unresolved' | 'resolved';
}

export interface ApprovalAction {
  label: string; // "Одобри имейла"
  variant?: 'primary' | 'secondary';
}

export interface MessageStep {
  kind: 'message';
  content: string; // markdown, стриймва се дума по дума
  time?: string; // само за seeded разговори
  citations?: Citation[];
  confidence?: Confidence;
  contradictions?: Contradiction[];
  approvalActions?: ApprovalAction[];
  streamWpm?: number; // опционална скорост
}

export type Step = UserStep | ThinkingStep | ToolCallStep | MessageStep;

export interface Conversation {
  id: string;
  title: string; // заглавие в списъка с разговори
  category: string; // "Ежедневие" | "Съответствие" | "Риск" | "Развитие" | "Клиент"
  label: string; // текст на чипа в „Нов чат“ (за живите разговори)
  client?: string; // "Ромил ООД" — контекстен етикет
  seeded?: { date: string }; // готова история със záдадена дата
  steps: Step[]; // започва с user стъпка
}

export const conversations: Conversation[] = [
  // ═══ SEEDED — готова история в списъка ═══════════════════════════════════
  {
    id: 'conv-dma',
    title: 'Смесена фактура ДМА — „Технополис“',
    category: 'Ежедневие',
    label: 'Как контирам смесена фактура за ДМА?',
    client: 'Ромил ООД',
    seeded: { date: '02.07' },
    steps: [
      {
        kind: 'user',
        content:
          'Фактура от "Технополис" за клиент "Ромил ООД" — лаптоп + докинг станция + 3г. гаранция на един ред. Как я контираме?',
        time: '09:14',
      },
      { kind: 'thinking', label: 'Търся конвенциите на клиента…' },
      {
        kind: 'tool',
        name: 'search_client_brain',
        args: { client: 'Ромил ООД', query: 'ДМА гаранция смесена фактура' },
        summary: 'Открита конвенция за разделяне на ДМА + правило #4471',
        durationMs: 1200,
      },
      {
        kind: 'tool',
        name: 'get_classification_rules',
        args: { client: 'Ромил ООД', counterparty: 'Технополис' },
        summary: '1 активно правило · потвърдено 8× · отменяно 0×',
        rows: [
          { поле: 'ДМА', третиране: 'гр. 20–21', увереност: 'висока' },
          { поле: 'разширена гаранция', третиране: 'разход бъдещи периоди', увереност: 'висока' },
        ],
        durationMs: 1000,
      },
      {
        kind: 'message',
        time: '09:15',
        content:
          'За **"Ромил ООД"** разделяме този тип фактури:\n\n' +
          '- Лаптопът е **ДМА** (гр. 20–21 по тяхната сметкоплан-конвенция)\n' +
          '- Докинг станцията минава като **консуматив**\n' +
          '- Разширената гаранция — **разход за бъдещи периоди**, не към стойността на актива\n\n' +
          '⚠️ Важи само когато гаранцията е отделно остойностена. Ако е "в пакет" без отделна цена — цялото отива в ДМА.',
        confidence: 'висока',
        citations: [
          {
            label: 'Правило #4471',
            source: 'Мариела · 12.02.2026',
            detail: 'прилагано 8×, потвърдено 8×, отменяно 0×',
          },
        ],
      },
      {
        kind: 'user',
        content: 'А ако гаранцията е „в пакет“, без отделна цена на реда?',
        time: '09:18',
      },
      { kind: 'thinking', label: 'Проверявам граничния случай…' },
      {
        kind: 'message',
        time: '09:18',
        content:
          'Тогава **цялата стойност отива в ДМА** — гаранцията се приема за част от придобивната цена, защото няма как да бъде надеждно отделена.\n\n' +
          'Ако искаш все пак да я отделиш, поискай от доставчика коригирана фактура с отделен ред — "Технополис" издават такава при запитване (правено е 2 пъти).',
        confidence: 'висока',
        citations: [
          { label: 'Правило #4471 · бележка', source: 'Мариела · 12.02.2026' },
        ],
      },
    ],
  },
  {
    id: 'conv-spidi',
    title: 'Задържана фактура от „Спиди“',
    category: 'Риск',
    label: 'Защо е задържана фактурата от „Спиди“?',
    client: 'Ромил ООД',
    seeded: { date: '03.07' },
    steps: [
      {
        kind: 'user',
        content: 'Защо е задържана фактура №2041 от „Спиди“ за "Ромил ООД"? Клиентът пита.',
        time: '11:02',
      },
      { kind: 'thinking', label: 'Сравнявам с историческия профил…' },
      {
        kind: 'tool',
        name: 'query_mirage',
        args: { client: 'Ромил ООД', query: 'фактури Спиди · 12-месечна история' },
        summary: '14 фактури · средна стойност 320 € · открито съвпадение с №2018',
        durationMs: 1500,
      },
      {
        kind: 'message',
        time: '11:03',
        content:
          'Задържах я по две причини:\n\n' +
          '1. **Сумата е 4 380 €** при средна стойност **320 €** за последните 13 фактури от „Спиди“ (13,7× отклонение)\n' +
          '2. Съвпада по сума и дата на доставка с **№2018 от 28.06** — възможен дубликат. „Спиди“ издават сборни фактури в края на месеца, които понякога дублират единични.\n\n' +
          'Ако се осчетоводи, разходът би се удвоил. Не мога да определя от документите коя е причината — трябва човешко решение.',
        confidence: 'ниска',
        contradictions: [
          {
            severity: 'hard',
            text: 'Ф-ра №2041 — вероятен дубликат на №2018 и 13,7× над профила. Автоматичното осчетоводяване е блокирано.',
            status: 'unresolved',
          },
        ],
        approvalActions: [
          { label: 'Задръж за преглед', variant: 'primary' },
          { label: 'Маркирай като ОК', variant: 'secondary' },
        ],
      },
      { kind: 'user', content: 'Какво да пиша на клиента междувременно?', time: '11:06' },
      {
        kind: 'message',
        time: '11:07',
        content:
          'Кратко и без аларма:\n\n' +
          '> Здравейте, фактура №2041 от „Спиди“ е временно задържана за рутинна проверка — сумата се различава значително от обичайните им фактури и проверяваме дали не дублира №2018 от 28.06. Ще я осчетоводим веднага щом потвърдим с тях. Няма нужда от действие от ваша страна.\n\n' +
          'Ако искаш, мога да я пратя директно от споделената поща.',
        confidence: 'висока',
      },
    ],
  },
  {
    id: 'conv-onboarding',
    title: 'Първа седмица — онбординг',
    category: 'Развитие',
    label: 'Направи ми чеклист за първата седмица',
    seeded: { date: '01.07' },
    steps: [
      {
        kind: 'user',
        content: 'Днес ми е първи ден. Направи ми онбординг чеклист за първата седмица — какво да науча и в какъв ред?',
        time: '08:47',
      },
      { kind: 'thinking', label: 'Сглобявам план от фирмените практики…' },
      {
        kind: 'tool',
        name: 'search_client_brain',
        args: { scope: 'фирмено ниво', query: 'онбординг нов служител очаквания' },
        summary: 'Открити бележки от последните 2 онбординга + очаквания на Мариела',
        durationMs: 1400,
      },
      {
        kind: 'tool',
        name: 'search_file_manager',
        args: { query: 'достъпи системи инструкции' },
        summary: 'Намерен: „Достъпи и системи.docx“ (обновен 06.2026)',
        durationMs: 900,
      },
      {
        kind: 'message',
        time: '08:49',
        content:
          'Ето чеклист за първата седмица, сглобен от това, което е работило при последните двама нови колеги:\n\n' +
          '**Ден 1–2: Ориентация**\n\n' +
          '☐ Достъпи: платформата, Mirage, споделената поща (списъкът е в „Достъпи и системи.docx“)  \n' +
          '☐ Прочети процедурата „Обработка на документи“ v3  \n' +
          '☐ Разгледай как е организиран файловият архив на 2–3 клиента\n\n' +
          '**Ден 3–4: Наблюдение**\n\n' +
          '☐ Мини през 10 вече прегледани фактури и виж защо са контирани така  \n' +
          '☐ Седни с Петя на едно ревю на живо\n\n' +
          '**Ден 5: Първи самостоятелни стъпки**\n\n' +
          '☐ Прегледай 5 фактури на клиент с малко специфики (препоръка: „Артемис Дизайн“)  \n' +
          '☐ Отбележи всеки въпрос тук — така се пълни паметта на кантората\n\n' +
          'Мариела очаква нови колеги да питат много — записаните въпроси са актив, не слабост.',
        confidence: 'средна',
        approvalActions: [{ label: 'Запази като мой чеклист', variant: 'primary' }],
        citations: [
          { label: 'Бележки от онбординг 03.2026', source: 'Мариела · 14.03.2026' },
          { label: 'Достъпи и системи.docx', source: 'файлов архив · 06.2026' },
        ],
      },
      { kind: 'user', content: 'Кой одобрява плащания и корекции, ако утре ми потрябва?', time: '08:53' },
      {
        kind: 'tool',
        name: 'search_client_brain',
        args: { scope: 'фирмено ниво', query: 'одобрения лимити отговорности' },
        summary: 'Намерена матрица на одобренията (v2, от май 2026)',
        rows: [
          { действие: 'плащане до 2 500 €', одобрява: 'старши счетоводител' },
          { действие: 'плащане над 2 500 €', одобрява: 'Мариела' },
          { действие: 'ново правило за контиране', одобрява: 'Мариела' },
          { действие: 'сторно / корекция в Mirage', одобрява: 'старши счетоводител' },
        ],
        durationMs: 1200,
      },
      {
        kind: 'message',
        time: '08:54',
        content:
          'Кратката версия:\n\n' +
          '- **Плащания до 2 500 €** → старши счетоводител (в момента Петя)\n' +
          '- **Плащания над 2 500 €** и **нови правила за контиране** → само Мариела\n' +
          '- **Сторно и корекции в Mirage** → старши счетоводител, с бележка защо\n\n' +
          'Блокираните от системата плащания ги отблокира само човекът с право за съответния размер — не се заобикалят ръчно.',
        confidence: 'висока',
        citations: [{ label: 'Матрица на одобренията v2', source: 'Мариела · 02.05.2026' }],
      },
    ],
  },
  {
    id: 'conv-vat',
    title: 'ДДС специфики — „Ромил ООД“',
    category: 'Развитие',
    label: 'Има ли специфики по ДДС при този клиент?',
    client: 'Ромил ООД',
    seeded: { date: '30.06' },
    steps: [
      { kind: 'user', content: 'Има ли специфики по ДДС, за които да внимавам при "Ромил ООД"?', time: '14:21' },
      { kind: 'thinking', label: 'Преглеждам ДДС историята и режимите…' },
      {
        kind: 'tool',
        name: 'search_client_brain',
        args: { client: 'Ромил ООД', query: 'ДДС режими частичен кредит ВОП' },
        summary: '3 записани ДДС специфики',
        durationMs: 1200,
      },
      {
        kind: 'tool',
        name: 'check_vies',
        args: { client: 'Ромил ООД', counterparty: 'Fischer GmbH (DE)' },
        summary: 'VIES: валиден DE ДДС номер на редовния доставчик',
        durationMs: 1300,
      },
      {
        kind: 'message',
        time: '14:22',
        content:
          'Три неща по ДДС при **"Ромил ООД"**:\n\n' +
          '1. **ВОП от Германия** — редовен доставчик *Fischer GmbH* (VIES номерът е валиден, проверявам го при всяка доставка)\n' +
          '2. **Частичен данъчен кредит** за разходите по автомобила под наем — прилага се коефициентът от договора за наем, не пълен кредит\n' +
          '3. **Авансите от клиенти** се фактурират в 5-дневен срок — Ромил често забравя и има настроено напомняне при постъпление без насрещна фактура\n\n' +
          'Дневниците се подават по стандартния календар, без отложени режими.',
        confidence: 'висока',
        citations: [
          { label: 'ДДС бележки', source: 'Мариела · 15.01.2026', detail: '3 записа, потвърдени при 2 декларации' },
        ],
      },
      { kind: 'user', content: 'Кога съставяме протокола по чл. 117 за доставките от Fischer?', time: '14:25' },
      {
        kind: 'message',
        time: '14:26',
        content:
          'При **получаване на фактурата, не при плащане** — това е записаната практика за Ромил. Срокът е до 15 дни от датата, на която данъкът е станал изискуем.\n\n' +
          'Практически: щом фактурата от Fischer влезе в системата, протоколът се генерира в същия месец, за да влязат покупката и самоначисляването в един данъчен период.',
        confidence: 'висока',
        citations: [{ label: 'ДДС бележки · ВОП', source: 'Мариела · 15.01.2026' }],
      },
    ],
  },
  {
    id: 'conv-fuel',
    title: 'Гориво на управителя — служебен разход',
    category: 'Ежедневие',
    label: 'Защо горивото на управителя е служебен разход?',
    client: 'Ромил ООД',
    seeded: { date: '04.07' },
    steps: [
      {
        kind: 'user',
        content: 'Защо за "Ромил ООД" горивото на личния автомобил на управителя се води служебен разход?',
        time: '10:31',
      },
      { kind: 'thinking', label: 'Търся записаното решение…' },
      {
        kind: 'tool',
        name: 'search_client_brain',
        args: { client: 'Ромил ООД', query: 'гориво личен автомобил управител' },
        summary: 'Намерено решение на Мариела от 03.11.2025 с обосновка',
        durationMs: 1300,
      },
      {
        kind: 'message',
        time: '10:32',
        content:
          'Защото при "Ромил ООД" случаят **не е личен автомобил в лична употреба**:\n\n' +
          '- Има **договор за наем** на автомобила между управителя и фирмата (от 01.11.2025)\n' +
          '- Води се **пътен лист** за всяко зареждане — колата се ползва за обекти на фирмата\n\n' +
          'Затова горивото е признат **служебен разход** с данъчен кредит по правилата за нает автомобил. Решението е на Мариела и се прилага без изключение оттогава.',
        confidence: 'висока',
        citations: [
          {
            label: 'Решение „гориво управител“',
            source: 'Мариела · 03.11.2025',
            detail: 'прилагано 5×, потвърдено 5×, отменяно 0×',
          },
        ],
      },
      { kind: 'user', content: 'А ако за някое зареждане липсва пътен лист?', time: '10:34' },
      {
        kind: 'message',
        time: '10:35',
        content:
          'Разходът **се отлага**, докато листът не бъде представен — това е част от същото решение. Не се осчетоводява „на доверие“ и не се прекласифицира като личен разход, просто изчаква.\n\n' +
          'В момента има едно такова висящо зареждане (28.06) — напомнянето към клиента е в седмичния дайджест.',
        confidence: 'висока',
        citations: [{ label: 'Решение „гориво управител“', source: 'Мариела · 03.11.2025' }],
      },
    ],
  },

  // ═══ LIVE — чипове в „Нов чат“, играят се на живо ═══════════════════════
  {
    id: 'live-restaurant',
    title: 'Ресторант — представителни или социални',
    category: 'Ежедневие',
    label: 'Ресторант — представителни или социални?',
    client: 'Ромил ООД',
    steps: [
      {
        kind: 'user',
        content: 'Фактура за ресторант на "Ромил ООД" — представителни разходи ли е или социални? Как ги различаваме тук?',
      },
      { kind: 'thinking', label: 'Преглеждам фирмената практика…' },
      {
        kind: 'tool',
        name: 'search_client_brain',
        args: { client: 'Ромил ООД', query: 'ресторант представителни социални разходи' },
        summary: 'Намерена фирмена практика с 2 потвърдени решения',
        durationMs: 1100,
      },
      {
        kind: 'message',
        content:
          'Практиката при "Ромил ООД", записана от Мариела:\n\n' +
          '- **Представителни** (10% данък върху разхода) — когато на срещата има **външни лица**: клиенти, партньори, доставчици. Това е случаят по подразбиране за ресторантски фактури.\n' +
          '- **Социални** — само за **вътрешни фирмени събития за целия персонал**.\n\n' +
          'Ако от фактурата не личи поводът — питаме клиента *преди* осчетоводяване. Мариела изрично е отбелязала, че НАП е проверявал тази граница при тях през 2024 г.',
        confidence: 'висока',
        citations: [
          { label: 'Практика „представителни“', source: 'Мариела · 18.12.2025', detail: 'прилагано 9×, потвърдено 9×' },
        ],
      },
      { kind: 'user', content: 'А коледното парти през декември?' },
      {
        kind: 'message',
        content:
          '**Социални разходи** — вътрешно събитие за целия персонал, класическият случай. Стига поканата да е към всички служители, не само към част от тях — иначе НАП може да го преквалифицира.\n\n' +
          'Миналогодишното парти на Ромил е минало точно така, без забележки.',
        confidence: 'висока',
        citations: [{ label: 'Практика „представителни“', source: 'Мариела · 18.12.2025' }],
      },
    ],
  },
  {
    id: 'live-avans',
    title: 'Крайна фактура с приспаднат аванс',
    category: 'Ежедневие',
    label: 'Крайна фактура с приспаднат аванс',
    client: 'Ромил ООД',
    steps: [
      {
        kind: 'user',
        content: 'Крайна фактура от "Строй Инвест" към "Ромил ООД" с приспаднат аванс от март. Как отразявам приспадането?',
      },
      { kind: 'thinking', label: 'Търся авансовата фактура…' },
      {
        kind: 'tool',
        name: 'query_mirage',
        args: { client: 'Ромил ООД', query: 'авансова фактура Строй Инвест март 2026' },
        summary: 'Намерена: ф-ра №0000000891 / 14.03.2026, аванс 12 500 € с ДДС',
        durationMs: 1500,
      },
      {
        kind: 'tool',
        name: 'search_client_brain',
        args: { client: 'Ромил ООД', query: 'приспадане аванс крайна фактура' },
        summary: 'Открита фирмена конвенция за приключване на аванси',
        durationMs: 900,
      },
      {
        kind: 'message',
        content:
          'Ето как приключваме аванса при крайната фактура:\n\n' +
          '1. Крайната фактура се осчетоводява в **пълния ѝ размер** по разхода/актива\n' +
          '2. Приспаднатият аванс **закрива вземането по предоставени аванси** (салдото по ф-ра №0000000891)\n' +
          '3. ДДС кредитът върху аванса вече е ползван през март — на крайната фактура се признава **само разликата**\n\n' +
          'Проверих: авансът е 12 500 € и салдото стои непокрито, така че сумите трябва да излязат точно.',
        confidence: 'висока',
        citations: [
          { label: 'Конвенция „аванси“', source: 'Мариела · 20.03.2026', detail: 'прилагано 4×, потвърдено 4×' },
        ],
      },
      { kind: 'user', content: 'Салдото не излиза точно — остава разлика от 120 €.' },
      { kind: 'thinking', label: 'Проверявам движенията по аванса…' },
      {
        kind: 'tool',
        name: 'query_mirage',
        args: { client: 'Ромил ООД', query: 'движения по сметка аванси Строй Инвест' },
        summary: 'Открито частично плащане 120 € от 02.04 извън аванса',
        durationMs: 1300,
      },
      {
        kind: 'message',
        content:
          'Намерих я: на **02.04** има **частично плащане от 120 €** към "Строй Инвест", осчетоводено директно срещу задължението, а не през аванса.\n\n' +
          'Приспадни аванса в пълния му размер (12 500 €), а разликата от 120 € се закрива срещу това плащане. Ако фактурата показва приспадане 12 620 € — поискай корекция от доставчика, при тях е грешката.',
        confidence: 'средна',
      },
    ],
  },
  {
    id: 'live-contradictions',
    title: 'Противоречия в правилата',
    category: 'Съответствие',
    label: 'Има ли противоречия в правилата на клиента?',
    client: 'Ромил ООД',
    steps: [
      { kind: 'user', content: 'Провери има ли противоречия в правилата за контиране на "Ромил ООД".' },
      { kind: 'thinking', label: 'Сканирам всички активни правила…' },
      {
        kind: 'tool',
        name: 'get_classification_rules',
        args: { client: 'Ромил ООД', scope: 'all' },
        summary: '27 активни правила заредени за анализ',
        durationMs: 1000,
      },
      {
        kind: 'tool',
        name: 'lint_contradictions',
        args: { client: 'Ромил ООД' },
        summary: 'Открити: 1 твърдо противоречие, 1 предупреждение',
        rows: [
          { тип: '🔴 твърдо', тема: 'гориво — две различни третирания', правила: '#2201 ↔ #4102' },
          { тип: '🟡 меко', тема: 'гаранции — рядко прилагано старо правило', правила: '#1088' },
        ],
        durationMs: 1700,
      },
      {
        kind: 'message',
        content:
          'Намерих **две находки** в правилата на "Ромил ООД":\n\n' +
          '**1. Гориво — твърдо противоречие.** Правило **#2201** (от 2025 г.) класифицира горивото като *разход за материали*, а по-новото **#4102** — като *разход за експлоатация на нает автомобил*. Двете се застъпват за фактури от ОМВ и блокират автоматичното контиране.\n\n' +
          '**2. Гаранции — меко предупреждение.** Старото правило **#1088** е прилагано само 2× и веднъж отменяно; конвенцията от февруари го замества на практика.\n\n' +
          'Мога да подготвя разрешаване: #2201 се архивира в полза на #4102 (то отразява договора за наем от ноември).',
        confidence: 'средна',
        contradictions: [
          {
            severity: 'hard',
            text: 'Правило #2201 и #4102 дават различно третиране за „гориво“ при един и същ контрагент (ОМВ) — автоматичното контиране е спряно.',
            status: 'unresolved',
          },
          {
            severity: 'soft',
            text: 'Правило #1088 („гаранции“) е практически заместено от конвенцията от 12.02.2026, но все още е активно.',
            status: 'unresolved',
          },
        ],
        citations: [
          { label: 'Правило #4102', source: 'Мариела · 05.11.2025', detail: 'прилагано 5×, потвърдено 5×' },
          { label: 'Правило #2201', source: 'Петя · 14.06.2025', detail: 'прилагано 12×, потвърдено 11×, отменяно 1×' },
        ],
      },
      { kind: 'user', content: 'Добре, архивирай #2201 тогава.' },
      {
        kind: 'message',
        content:
          'Подготвих архивирането на **#2201** — но промяната на правило изисква одобрение от Мариела (по матрицата на одобренията).\n\n' +
          'Изпращам ѝ заявката с пълната история на двете правила и обосновката. Дотогава фактурите от ОМВ остават за ръчен преглед.',
        confidence: 'висока',
        approvalActions: [
          { label: 'Изпрати за одобрение', variant: 'primary' },
          { label: 'Отказ', variant: 'secondary' },
        ],
      },
    ],
  },
  {
    id: 'live-iban',
    title: 'Смяна на IBAN — „Мега Строй“',
    category: 'Риск',
    label: 'Нещо нередно с плащанията тази седмица?',
    client: 'Ромил ООД',
    steps: [
      { kind: 'user', content: 'Има ли нещо нередно около плащанията към доставчици на "Ромил ООД" тази седмица?' },
      { kind: 'thinking', label: 'Проверявам банковите детайли по фактурите…' },
      {
        kind: 'tool',
        name: 'get_counterparty_mappings',
        args: { client: 'Ромил ООД', period: 'тази седмица' },
        summary: '11 плащания планирани · 1 несъответствие в банкови детайли',
        durationMs: 1400,
      },
      {
        kind: 'tool',
        name: 'search_emails',
        args: { client: 'Ромил ООД', query: 'Мега Строй смяна банкова сметка' },
        summary: 'Няма имейл от Мега Строй за смяна на сметка',
        durationMs: 1200,
      },
      {
        kind: 'message',
        content:
          '⚠️ Едно нещо изисква **незабавно внимание**:\n\n' +
          'Фактурата от **„Мега Строй“** тази седмица е с **нов IBAN**, различен от този по предишните **14 фактури**. Проверих пощата — **няма официално уведомление** за смяна на сметка.\n\n' +
          'Смяна на IBAN без уведомление е класически белег на **фактурна измама** (компрометирана поща на доставчика). Плащането от 8 940 € е спряно, докато някой не потвърди новата сметка **по телефон, на познат номер** — не на телефона от фактурата.\n\n' +
          'Останалите 10 плащания са към познати сметки и минават нормално.',
        confidence: 'висока',
        contradictions: [
          {
            severity: 'hard',
            text: 'Нов IBAN за „Мега Строй“ без уведомление — плащане от 8 940 € е блокирано до ръчно потвърждение.',
            status: 'unresolved',
          },
        ],
      },
      { kind: 'user', content: 'Позвъних им на офисния номер — потвърдиха, че сметката е сменена.' },
      {
        kind: 'message',
        content:
          'Чудесно. Записвам потвърждението (телефонно, на познат номер, от теб) и **отблокирам плащането** след твоето финално одобрение.\n\n' +
          'Новият IBAN става доверен за бъдещи фактури от „Мега Строй“ — следващата им фактура няма да задейства проверката.',
        confidence: 'висока',
        approvalActions: [
          { label: 'Потвърди новия IBAN и плати', variant: 'primary' },
          { label: 'Задръж още', variant: 'secondary' },
        ],
      },
    ],
  },
  {
    id: 'live-takeover',
    title: 'Поемане на „Ромил ООД“',
    category: 'Развитие',
    label: 'Поемам клиента от днес — какво да знам?',
    client: 'Ромил ООД',
    steps: [
      { kind: 'user', content: 'Поемам "Ромил ООД" от днес — какво трябва да знам, преди да пипна каквото и да е?' },
      { kind: 'thinking', label: 'Събирам институционалната памет за клиента…' },
      {
        kind: 'tool',
        name: 'search_client_brain',
        args: { client: 'Ромил ООД', query: 'всички конвенции решения специфики' },
        summary: '27 правила · 9 решения на Мариела · 3 известни капана',
        durationMs: 1500,
      },
      {
        kind: 'tool',
        name: 'get_classification_rules',
        args: { client: 'Ромил ООД', sort: 'по честота' },
        summary: 'Топ конвенции по честота на прилагане',
        rows: [
          { конвенция: 'ДМА се разделя от гаранции/аксесоари', прилагано: '8×', автор: 'Мариела' },
          { конвенция: 'гориво на управителя = служебен разход', прилагано: '5×', автор: 'Мариела' },
          { конвенция: 'лизинг Интерлийз — 3 отделни пера', прилагано: '11×', автор: 'Мариела' },
          { конвенция: 'куриерски = външни услуги', прилагано: '14×', автор: 'Петя' },
        ],
        durationMs: 1100,
      },
      {
        kind: 'message',
        content:
          'Ето краткия брифинг за **"Ромил ООД"** (търговия + монтаж, ~40 фактури/месец):\n\n' +
          '**Специфики, които ще срещнеш веднага:**\n' +
          '- Смесени фактури за техника се **разделят**: ДМА отделно от гаранции и аксесоари\n' +
          '- Горивото на управителя е **служебен разход** (има договор за наем + пътни листове — не го "поправяй")\n' +
          '- Лизингът от Интерлийз се контира на **3 отделни пера** всеки месец\n\n' +
          '**Известни капани:**\n' +
          '- „Спиди“ понякога издава дубликати — сверявай сборните фактури\n' +
          '- Ресторантските фактури са представителни **по подразбиране** (НАП ги е проверявал за това)\n' +
          '- Има **неразрешено противоречие** за горивото (#2201 ↔ #4102) — не контирай фактури от ОМВ, преди да се разреши\n\n' +
          '**Кой какво е решавал:** повечето конвенции са на Мариела; куриерските са практика на Петя. При съмнение — питай тук първо, всяко решение е с история.',
        confidence: 'висока',
        citations: [
          { label: 'Правило #4471 (ДМА)', source: 'Мариела · 12.02.2026', detail: 'прилагано 8×' },
          { label: 'Решение „гориво управител“', source: 'Мариела · 03.11.2025', detail: 'прилагано 5×' },
          { label: 'Правило #3120 (лизинг)', source: 'Мариела · 08.09.2025', detail: 'прилагано 11×' },
        ],
      },
      { kind: 'user', content: 'Кои контрагенти са най-проблемни?' },
      {
        kind: 'tool',
        name: 'get_counterparty_mappings',
        args: { client: 'Ромил ООД', sort: 'по брой корекции' },
        summary: 'Топ 3 контрагента по ръчни корекции за 12 месеца',
        rows: [
          { контрагент: 'Спиди', корекции: '6', причина: 'дубликати и сборни фактури' },
          { контрагент: 'Технополис', корекции: '4', причина: 'смесени редове ДМА + услуги' },
          { контрагент: 'Мега Строй', корекции: '3', причина: 'аванси без ясно обвързване' },
        ],
        durationMs: 1400,
      },
      {
        kind: 'message',
        content:
          'Трима заслужават повишено внимание:\n\n' +
          '- **„Спиди“** — най-много корекции (6). Сборни фактури в края на месеца, които понякога дублират единични\n' +
          '- **„Технополис“** — смесени фактури; важи конвенцията за разделяне на ДМА\n' +
          '- **„Мега Строй“** — аванси без ясно обвързване + тази седмица **непотвърдена смяна на IBAN** (виж отворения риск-сигнал)\n\n' +
          'Останалите 28 контрагента вървят гладко с автоматичното контиране.',
        confidence: 'висока',
        citations: [
          { label: 'История на корекциите', source: 'системен анализ · 12 месеца', detail: '13 корекции при 31 контрагента' },
        ],
      },
    ],
  },
  {
    id: 'live-newclient',
    title: 'Нов клиент — „Артемис Дизайн“',
    category: 'Клиент',
    label: 'Какви специфики да заложим за нов клиент?',
    client: 'Артемис Дизайн ЕООД',
    steps: [
      {
        kind: 'user',
        content: 'Поемаме нов клиент — "Артемис Дизайн ЕООД". Какво знаем за тях и какви специфики да заложим от старта?',
      },
      { kind: 'thinking', label: 'Събирам наличното от документите…' },
      {
        kind: 'tool',
        name: 'check_vies',
        args: { counterparty: 'Артемис Дизайн ЕООД' },
        summary: 'ЕИК 207114458 · валидна ДДС регистрация от 03.2024',
        durationMs: 1300,
      },
      {
        kind: 'tool',
        name: 'search_file_manager',
        args: { client: 'Артемис Дизайн ЕООД', query: 'договор за счетоводно обслужване' },
        summary: 'Намерен договор от 01.07.2026 + приемо-предавателен протокол',
        durationMs: 1100,
      },
      {
        kind: 'message',
        content:
          'Ето какво **знаем** и какво **липсва** за "Артемис Дизайн ЕООД":\n\n' +
          '**Знаем от документите:**\n' +
          '- Дейност: графичен дизайн и брандинг услуги (основно B2B)\n' +
          '- ДДС регистрация от 03.2024, валидна (проверена днес)\n' +
          '- Обслужваме ги от 01.07.2026; началните салда са прехвърлени\n\n' +
          '**Не знаем още** (тук съм с ниска увереност — няма история, от която да уча):\n' +
          '- Има ли автомобили и как се ползват\n' +
          '- Практика за авансови плащания от клиентите им\n' +
          '- Абонаменти от чужбина (вероятни при дизайн студио — внимание за протоколи по чл. 117)',
        confidence: 'ниска',
        citations: [
          { label: 'Договор за обслужване', source: 'файлов архив · 01.07.2026' },
          { label: 'VIES проверка', source: 'днес' },
        ],
      },
      { kind: 'user', content: 'Подготви ми въпросите за първата среща с тях.' },
      {
        kind: 'message',
        content:
          'Ето списъка — всеки отговор ще запиша като начална конвенция:\n\n' +
          '1. **Автомобили** — има ли фирмени или лични в служебна употреба? Договори, пътни листове?\n' +
          '2. **Чуждестранни доставчици** — Adobe, шрифтове, стокови снимки? (определя чл. 117 протоколите)\n' +
          '3. **Аванси** — искат ли аванс от клиентите си и как го фактурират?\n' +
          '4. **Банкови сметки** — всички сметки, включително Revolut/Wise, ако ползват\n' +
          '5. **Дълготрайни активи** — техника над прага, лицензи, кой ги одобрява\n\n' +
          'Мога да ги пратя на клиента предварително по имейл, за да дойдат подготвени.',
        confidence: 'висока',
        approvalActions: [
          { label: 'Запази в досието', variant: 'primary' },
          { label: 'Прати на клиента', variant: 'secondary' },
        ],
      },
    ],
  },
  {
    id: 'live-digest',
    title: 'Седмичен дайджест — „Ромил ООД“',
    category: 'Клиент',
    label: 'Подготви седмичния дайджест за клиента',
    client: 'Ромил ООД',
    steps: [
      { kind: 'user', content: 'Подготви седмичния дайджест за "Ромил ООД" — какво да им пратим този петък?' },
      { kind: 'thinking', label: 'Събирам данните за седмицата…' },
      {
        kind: 'tool',
        name: 'get_turnover',
        args: { client: 'Ромил ООД', period: '29.06 – 05.07.2026' },
        summary: 'Приходи 42 300 € (+8% с/о мин. седмица) · разходи 31 150 €',
        durationMs: 1400,
      },
      {
        kind: 'tool',
        name: 'search_emails',
        args: { client: 'Ромил ООД', query: 'отворени въпроси тази седмица' },
        summary: '1 отворен въпрос: липсващ пътен лист за 28.06',
        durationMs: 1000,
      },
      {
        kind: 'message',
        content:
          'Ето черновата, в предпочитания от тях формат (кратко, 3 секции):\n\n' +
          '---\n\n' +
          '**До:** г-н Ромилов, "Ромил ООД"\n\n' +
          '**Относно:** Седмичен преглед 29.06 – 05.07\n\n' +
          'Здравейте,\n\n' +
          '**Числата тази седмица:** приходи 42 300 € (+8% спрямо миналата седмица), разходи 31 150 €. Обработихме 8 фактури.\n\n' +
          '**Чакаме от вас:** пътен лист за зареждането от 28.06 — без него разходът за гориво остава отложен.\n\n' +
          '**За ваше внимание:** задържали сме фактура от „Спиди“ за 4 380 € за проверка (вероятен дубликат) и плащане към „Мега Строй“ заради непотвърдена смяна на банкова сметка. Ще ви позвъним за второто.\n\n' +
          'Хубав уикенд,\nЕкипът на кантората\n\n' +
          '---',
        confidence: 'висока',
        approvalActions: [
          { label: 'Одобри имейла', variant: 'primary' },
          { label: 'Редактирай', variant: 'secondary' },
        ],
        citations: [
          { label: 'Предпочитания за комуникация', source: 'Мариела · 09.01.2026', detail: 'кратко, 3 секции, петък сутрин' },
        ],
      },
      { kind: 'user', content: 'Съкрати го — само числата и какво чакаме от тях.' },
      {
        kind: 'message',
        content:
          'Готово:\n\n' +
          '---\n\n' +
          'Здравейте,\n\n' +
          'Седмицата накратко: **приходи 42 300 €** (+8%), **разходи 31 150 €**, 8 фактури обработени.\n\n' +
          'Чакаме от вас само пътния лист за зареждането от 28.06.\n\n' +
          'Хубав уикенд,\nЕкипът\n\n' +
          '---\n\n' +
          'Бележка: махнах секцията за задържаните позиции — ако предпочиташ да остане поне един ред за „Спиди“, кажи.',
        confidence: 'висока',
        approvalActions: [
          { label: 'Одобри имейла', variant: 'primary' },
        ],
      },
    ],
  },
];

export const seededConversations = conversations.filter((c) => c.seeded);
export const liveConversations = conversations.filter((c) => !c.seeded);
